// client/src/services/MatchService.js
import BaseService from './BaseService'
import flex from '../api/FlexFetch'
import {
  getMatchStatusSummary as utilMatchSummary,
  formatMatchScore as utilFormatScore,
  getOpponentName as utilOpponentName,
  getPlayerScore as utilGetPlayerScore,
  getOpponentScore as utilGetOpponentScore,
  getMatchOutcomeForPlayer as utilGetMatchOutcomeForPlayer,
} from '../utils/match'

/**
 * Service for managing match operations - scoring, scheduling, and match details
 */
class MatchService extends BaseService {
  constructor() {
    super()
    this.endpoint = '/api/1.0/matches/'
  }

  /**
   * Submit match scores
   * @param {number} matchId - ID of the match
   * @param {Object} scoreData - Score data containing sets and status
   * @returns {Promise<Object>} Service response object
   */
  async submitScore(matchId, scoreData) {
    return this.execute(
      async () => {
        this.validateParams({ matchId, scoreData }, ['matchId', 'scoreData'])

        const { sets, status } = scoreData
        this.validateParams({ sets, status }, ['sets', 'status'])

        const response = await flex.flexFetch(
          this.endpoint + `submitScores?matchId=${matchId}`,
          {
            method: 'POST',
            body: JSON.stringify({
              sets,
              status,
            }),
          },
        )

        return await flex.handleResponse(response)
      },
      'Submit match score',
      { dedupeKey: null },
    ) // Disable deduplication for mutations
  }

  /**
   * Get scores for a match
   * @param {number} matchId - ID of the match
   * @returns {Promise<Object>} Service response object with score data
   */
  async getScores(matchId) {
    return this.execute(
      async () => {
        this.validateParams({ matchId }, ['matchId'])

        const response = await flex.flexFetch(
          this.endpoint + `getScores?matchId=${matchId}`,
        )

        return await flex.handleResponse(response)
      },
      'Get match scores',
      { dedupeKey: `match-scores-${matchId}` },
    ) // Dedupe by match ID
  }

  /**
   * Schedule or reschedule a match
   * @param {number} matchId - ID of the match
   * @param {string} matchDatetime - Match datetime (ISO string)
   * @returns {Promise<Object>} Service response object
   */
  async scheduleMatch(matchId, matchDatetime) {
    return this.execute(
      async () => {
        this.validateParams({ matchId, matchDatetime }, [
          'matchId',
          'matchDatetime',
        ])

        const response = await flex.flexFetch(this.endpoint + 'scheduleMatch', {
          method: 'POST',
          body: JSON.stringify({
            match_id: matchId,
            match_datetime: matchDatetime,
          }),
        })

        return await flex.handleResponse(response)
      },
      'Schedule match',
      { dedupeKey: null },
    ) // Disable deduplication for mutations
  }

  /**
   * Get match details
   * @param {number} matchId - ID of the match
   * @returns {Promise<Object>} Service response object with match details
   */
  async getMatch(matchId) {
    return this.execute(async () => {
      this.validateParams({ matchId }, ['matchId'])

      const response = await flex.flexFetch(
        this.endpoint + `getMatchDetails?match_id=${matchId}`,
      )

      return await flex.handleResponse(response)
    }, 'Get match details')
  }

  /**
   * Bulk load scores for multiple matches
   * @param {Array} matchIds - Array of match IDs
   * @returns {Promise<Object>} Service response object with all match scores
   */
  async bulkGetScores(matchIds) {
    return this.execute(async () => {
      this.validateParams({ matchIds }, ['matchIds'])

      const results = {}
      const errors = []

      await Promise.all(
        matchIds.map(async (matchId) => {
          try {
            const scoreResponse = await this.getScores(matchId)
            if (scoreResponse.success) {
              results[matchId] = scoreResponse.data
            } else {
              errors.push({ matchId, error: scoreResponse.error })
            }
          } catch (error) {
            errors.push({ matchId, error: error.message })
          }
        }),
      )

      return {
        scores: results,
        errors,
        successCount: Object.keys(results).length,
        errorCount: errors.length,
      }
    }, 'Bulk get match scores')
  }

  /**
   * Get match status summary
   * @param {Array} matches - Array of match objects
   * @returns {Promise<Object>} Service response object with match status summary
   */
  async getMatchStatusSummary(matches) {
    return this.execute(async () => {
      this.validateParams({ matches }, ['matches'])
      return utilMatchSummary(matches)
    }, 'Get match status summary')
  }

  /**
   * Format match score for display
   * @param {Object} match - Match object with set_scores
   * @param {number} currentPlayerId - ID of the current player
   * @returns {string} Formatted score string
   */
  formatMatchScore(match, currentPlayerId) {
    return utilFormatScore(match, currentPlayerId)
  }

  /**
   * Get opponent name for a match
   * @param {Object} match - Match object
   * @param {number} currentPlayerId - ID of the current player
   * @returns {string} Opponent's name
   */
  getOpponentName(match, currentPlayerId) {
    return utilOpponentName(match, currentPlayerId)
  }

  /**
   * Get player's total score for a match
   * @param {Object} match - Match object with set_scores
   * @param {number} playerId - ID of the player
   * @returns {number} Total games won by the player
   */
  getPlayerScore(match, playerId) {
    return utilGetPlayerScore(match, playerId)
  }

  /**
   * Get opponent's total score for a match
   * @param {Object} match - Match object with set_scores
   * @param {number} playerId - ID of the current player
   * @returns {number} Total games won by the opponent
   */
  getOpponentScore(match, playerId) {
    return utilGetOpponentScore(match, playerId)
  }

  /**
   * Determine match outcome for a player, including retirement scenarios
   * @param {Object} match
   * @param {number} playerId
   * @returns {'win'|'loss'|'unknown'}
   */
  getMatchOutcomeForPlayer(match, playerId) {
    return utilGetMatchOutcomeForPlayer(match, playerId)
  }

  /**
   * Get opponent's phone number from match details
   * @param {Object} match - Match object with player IDs
   * @param {Object} matchDetails - Full match details with phone numbers
   * @returns {string|null} Opponent's phone number or null
   */
  getOpponentPhone(match, matchDetails) {
    if (!matchDetails || !match || !match.current_player_id) return null

    // Determine if current player is home or away
    const isHome = match.home_player_id === match.current_player_id
    return isHome ? matchDetails.away_phone : matchDetails.home_phone
  }

  /**
   * Get the next scheduled match for the current user
   * @returns {Promise<Object>} Service response with next match or null
   */
  async getNextMatch() {
    return this.execute(async () => {
      const response = await flex.flexFetch(this.endpoint + 'getNextMatch')
      return await flex.handleResponse(response)
    }, 'Get next match')
  }

  /**
   * Cancel a match (delete it from the system)
   * @param {number} matchId - ID of the match to cancel
   * @returns {Promise<Object>} Service response object
   */
  async cancelMatch(matchId) {
    return this.execute(async () => {
      this.validateParams({ matchId }, ['matchId'])

      const response = await flex.flexFetch(this.endpoint + 'cancelMatch', {
        method: 'DELETE',
        body: JSON.stringify({
          match_id: matchId,
        }),
      })

      return await flex.handleResponse(response)
    }, 'Cancel match')
  }
}

// Export singleton instance
const matchService = new MatchService()
export default matchService
